import { useMemo } from "react";
import { EvidencePackViewerDTO } from "@deepguide-ai/types";
import { FilePreview, getFileType } from "./FilePreview";
import { Eye } from "lucide-react";

interface MainPreviewAreaProps {
  pack: EvidencePackViewerDTO;
  selectedFile: { type: 'screenshot' | 'attachment'; id: string } | null;
  highlightParagraphs?: number[];
  highlightText?: string;
  pdfPage?: number | null;
  highlightBox?: { x: number; y: number; width: number; height: number } | null;
  selectedRequirementId?: string | null;
}

export default function MainPreviewArea({
  pack,
  selectedFile,
  highlightParagraphs,
  highlightText,
  pdfPage,
  highlightBox,
  selectedRequirementId: _selectedRequirementId,
}: MainPreviewAreaProps) {

  const { artifacts } = pack;

  // Find the selected file
  const fileToPreview = useMemo(() => {
    if (!selectedFile) return null;

    if (selectedFile.id === 'video' && artifacts.video) {
      return {
        file: { url: artifacts.video.url, filename: 'Session Recording' },
        type: 'video' as const,
      };
    }

    if (selectedFile.type === 'screenshot') {
      const screenshot = artifacts.screenshots.find((s) => s.id === selectedFile.id);
      if (screenshot) {
        return { file: screenshot, type: 'image' as const };
      }
    }

    if (selectedFile.type === 'attachment') {
      const attachment = artifacts.attachments.find((a) => a.id === selectedFile.id);
      if (attachment) {
        return {
          file: attachment,
          type: getFileType(attachment.mimeType, attachment.filename),
        };
      }
    }

    return null;
  }, [selectedFile, artifacts]);

  if (!fileToPreview) {
    return (
      <div className="flex h-full w-full flex-1 flex-col items-center justify-center rounded-md bg-muted/20 gap-4">
        <Eye className="h-12 w-12 text-muted-foreground/50" />
        <div className="text-center">
          <div className="text-sm font-medium">Select a file to preview</div>
          <div className="text-xs text-muted-foreground mt-1">
            Click on a screenshot or attachment from the sidebar
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full min-h-0 flex-1 flex-col">
      
      {/* File Preview */}
      <div className="flex-1 min-h-0">
        <FilePreview
          file={fileToPreview.file}
          type={fileToPreview.type}
          highlightParagraphs={highlightParagraphs}
          highlightText={highlightText}
          pageNumber={fileToPreview.type === 'pdf' ? pdfPage ?? undefined : undefined}
          highlightBox={fileToPreview.type === 'image' ? highlightBox : undefined}
        />
      </div>
    </div>
  );
}
